import { useState, useRef } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Divider from "@amzn/meridian/esm/components/divider";
import Icon from "@amzn/meridian/esm/components/icon";
import Loader from "@amzn/meridian/esm/components/loader";
import Textarea from "@amzn/meridian/esm/components/textarea";
import Popover from "@amzn/meridian/esm/components/popover";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import Tooltip from "@amzn/meridian/esm/components/tooltip";
import sendSmallTokens from "@amzn/meridian-tokens/base/icon/send-small";
import chevronDownTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import newChatIcon from "../../../images/new-chat.svg";
import { getAgentHandle, truncateDisplayName } from "../../../utils/agentDisplayUtils";
import { fetchActiveAgents } from "../../../utils/agenticExtensionUtils";
import { ROLES } from "../../../constants";
import { THEME_COLOR } from "../../../styles/colors";
import aimsTheme from "../../../styles/meridian-theme-aims.json";
import ChatDisclaimerFooter from "../ChatDisclaimerFooter";

// A leading "@shortname" picks that agent. Anchored to the whole input on purpose: an "@" that
// isn't the entire message is ordinary text (emails, "ping @ 3pm"). The optional trailing space
// is what the user types to confirm.
const AGENT_HANDLE = /^@([a-zA-Z0-9_-]+)[ \t]?$/;

const matchAgentHandle = (agents, text) => {
  const match = AGENT_HANDLE.exec(text);
  return match ? (agents || []).find((a) => a.shortName?.toLowerCase() === match[1].toLowerCase()) : null;
};

const ChatInput = ({ onSend, onNewSession, disabled, agents, selectedAgent, onSelectAgent, role, meetingUuid, onAgentsRefreshed }) => {
  const [message, setMessage] = useState("");
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const agentButtonRef = useRef(null);
  const agentFetchIdRef = useRef(0);

  const handleSend = () => {
    if (disabled || !message.trim()) return;
    if (onSend) onSend(message.trim());
    setMessage("");
  };

  // Space or Enter after a leading "@shortname" confirms it: select that agent and clear the
  // handle so it never goes to the model as prompt text. Returns whether it consumed the input.
  const confirmAgentHandle = (text) => {
    const agent = matchAgentHandle(agents, text);
    if (!agent) return false;
    setMessage("");
    if (agent.id !== selectedAgent?.id) onSelectAgent(agent.id);
    return true;
  };

  const handleMessageChange = (value) => {
    if (value.endsWith(" ") && confirmAgentHandle(value)) return;
    setMessage(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (confirmAgentHandle(message)) return;
      handleSend();
    }
  };

  const handleAgentSelect = (agent) => {
    onSelectAgent(agent.id);
    setAgentMenuOpen(false);
  };

  const handleOpenAgentMenu = () => {
    setAgentMenuOpen(true);
    if (role !== ROLES.ORGANIZER && meetingUuid) {
      setAgentsLoading(true);
      const fetchId = ++agentFetchIdRef.current;
      fetchActiveAgents(meetingUuid).then((activeAgents) => {
        if (fetchId !== agentFetchIdRef.current) return;
        // null means the fetch failed — keep showing the last known list.
        if (activeAgents && onAgentsRefreshed) onAgentsRefreshed(activeAgents);
      }).finally(() => {
        if (fetchId === agentFetchIdRef.current) setAgentsLoading(false);
      });
    }
  };

  return (
    <Column width="318px" spacing="none">
      <Theme tokens={{ boxBorderRadiusOutline: 10, boxBorderColorOutline: THEME_COLOR.MIST_GRAY }}>
      <Box type="outline" width="318px" className="chat-input-box">
        <Column spacing="none">
          <Row widths={["fit", "fit"]} alignmentHorizontal="justify" spacing="none">
            {agents && agents.length > 0 && selectedAgent ? (
              <>
                <Button
                  ref={agentButtonRef}
                  type="link"
                  size="small"
                  onClick={() => {
                    if (agentMenuOpen) {
                      setAgentMenuOpen(false);
                    } else {
                      handleOpenAgentMenu();
                    }
                  }}
                >
                  <Row spacing="200" alignmentHorizontal="start">
                    <Icon tokens={sparklesSmallTokens} color="success" />
                    <Text type="b100">{truncateDisplayName(selectedAgent.name)}</Text>
                    <Text type="b100" color="secondary">{getAgentHandle(selectedAgent)}</Text>
                    <Icon tokens={chevronDownTokens} color="secondary" />
                  </Row>
                </Button>
                <Popover
                  anchorNode={agentButtonRef.current}
                  open={agentMenuOpen}
                  onClose={() => setAgentMenuOpen(false)}
                  position="top"
                  alignment="start"
                  spacingInset="none"
                  indicatorArrow={false}
                >
                  <Theme tokens={{
                    buttonForegroundColorTertiaryDefault: THEME_COLOR.BLACK,
                    buttonBorderColorTertiaryDefault: "transparent",
                    buttonBorderColorTertiaryHover: "transparent",
                    buttonBorderColorTertiaryPressed: "transparent",
                    buttonSpacingInsetHorizontalMedium: 12,
                    buttonLabelMediumFontSize: 14,
                  }}>
                  <Column spacing="none" width="280px">
                    {agents.map((agent) => (
                      <Button key={agent.id} type="tertiary" size="medium" onClick={() => handleAgentSelect(agent)}>
                        <Row spacing="200" alignmentHorizontal="start" width="280px">
                          <Text type="b100">{truncateDisplayName(agent.name)} {getAgentHandle(agent)}</Text>
                        </Row>
                      </Button>
                    ))}
                    {agentsLoading && (
                      <Row alignmentHorizontal="center" spacingInset="300">
                        <Theme palette={aimsTheme}>
                          <Loader size="small" />
                        </Theme>
                      </Row>
                    )}
                  </Column>
                  </Theme>
                </Popover>
              </>
            ) : <Row />}

            <Row spacing="none" alignmentVertical="center" spacingInset="none 300 none none">
              <Theme tokens={{ buttonHeightSmall: 20, buttonSpacingInsetHorizontalSmall: 2 }}>
                <Tooltip position="top" title="New chat" id="newChatTooltip">
                  <Button type="icon" size="small" onClick={onNewSession} disabled={disabled} aria-describedby="newChatTooltip">
                      <img src={newChatIcon} alt="New chat"/>
                  </Button>
                </Tooltip>
              </Theme>
            </Row>
          </Row>

          <Theme tokens={{ dividerColor: THEME_COLOR.MIST_GRAY }}>
            <Divider />
          </Theme>

          <Column heights={["fill", "fit"]} spacing="none">
            <div className="chat-input-no-shadow" onKeyDown={handleKeyDown}>
              <Theme tokens={{
                inputTextSmallFontSize: 12,
                inputTextSmallLineHeight: "18px",
                inputLabelColorFocus: THEME_COLOR.TEAL_GREEN,
              }}>
                <Textarea
                  value={message}
                  onChange={handleMessageChange}
                  placeholder="Ask assistant"
                  rows={1}
                  size="small"
                  resize="none"
                />
              </Theme>
            </div>
            <Row alignmentHorizontal="end" spacingInset="none 300 300 none">
              <Theme tokens={{
                buttonHeightSmall: 28,
                buttonBorderRadius: 999,
                buttonSpacingInsetHorizontalSmall: 0,
              }}>
                <Button type="primary" size="small" onClick={handleSend} disabled={disabled || !message.trim()} aria-label="Send" minWidth="28px">
                  <div style={{ transform: "scale(0.7)", display: "flex" }}>
                    <Icon tokens={sendSmallTokens} />
                  </div>
                </Button>
              </Theme>
            </Row>
          </Column>
        </Column>
      </Box>
      </Theme>
      <ChatDisclaimerFooter />
    </Column>
  );
};

export default ChatInput;
